import { Link } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";

export interface BreadcrumbItem {
  /** Visible anchor text (use descriptive, keyword-rich phrasing for SEO) */
  label: string;
  /** Route path; omit for the current page (rendered as plain text) */
  to?: string;
}

const SITE_URL = "https://texasmadeshade.com";

/**
 * SEO-friendly breadcrumbs with descriptive anchor text + BreadcrumbList JSON-LD.
 * Always prepends "Home" linking to "/".
 */
export function Breadcrumbs({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
  const trail: BreadcrumbItem[] = [{ label: "Home", to: "/" }, ...items];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.label,
      item: item.to ? `${SITE_URL}${item.to}` : undefined,
    })),
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={`border-b border-navy-deep/10 bg-cream/60 ${className ?? ""}`}
    >
      <div className="mx-auto max-w-7xl px-4 py-3 lg:px-8">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-navy-deep/80">
          {trail.map((item, i) => {
            const isLast = i === trail.length - 1;
            return (
              <li key={`${item.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-navy-deep/70" aria-hidden="true" />}
                {isLast || !item.to ? (
                  <span className="font-display font-semibold text-navy-deep" aria-current="page">
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    className="inline-flex items-center gap-1 text-navy-deep underline-offset-2 transition hover:text-red hover:underline"
                  >
                    {i === 0 && <Home className="h-3.5 w-3.5" aria-hidden="true" />}
                    {item.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
}
